import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, X, UserCheck, UserX, Trash2, Save, ShieldAlert,
  Mail, Send, KeyRound, Clock, Globe, MoreHorizontal, RotateCcw, UserCog,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { api } from "../../lib/api";
import { accessOf, roleChangeQuestion } from "../../lib/userRoles";
import { ROLE_LABEL } from "../../lib/roleLabels";

// Canonical roles per Phase 1 RBAC. "viewer" is kept for backwards-compat (light user),
// "admin" is legacy alias mapped server-side to "admin_fped".
const ALL_ROLES = ["owner", "admin_fped", "partner_operator", "viewer"];
const ROLE_COLORS = {
  owner: "bg-[#965EA7]/15 text-[#7A4986]",
  admin_fped: "bg-[#3F8EBA]/15 text-[#1f5d80]",
  partner_operator: "bg-[#D97706]/15 text-[#8b5a06]",
  viewer: "bg-[#6B7280]/15 text-[#373A45]",
  admin: "bg-[#3F8EBA]/15 text-[#1f5d80]", // legacy
};

const STATUS_LABEL = { active: "Activ", invited: "Invitat", disabled: "Dezactivat" };
const STATUS_COLORS = {
  active: "bg-[#519872]/15 text-[#2f6b50]",
  invited: "bg-[#D97706]/15 text-[#8b5a06]",
  disabled: "bg-[#9b1f2c]/15 text-[#9b1f2c]",
};

const CHANNEL_LABEL = { google: "Google", magic_link: "Magic Link" };
const CHANNEL_COLORS = {
  google: "bg-[#4285F4]/10 text-[#1a56b8]",
  magic_link: "bg-[#965EA7]/10 text-[#7A4986]",
};

function deriveStatus(u) {
  return u.status || (u.active ? "active" : "disabled");
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ro-RO");
  } catch {
    return iso;
  }
}

export default function AdminUsers({ isOwner, currentEmail, currentRole }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ email: "", name: "", role: "viewer", assigned_org: "" });
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);

  // admin_fped users can invite but only partner_operator/viewer. Owner can invite anyone.
  const canInvite = isOwner || currentRole === "admin_fped";
  // Mutations on existing rows remain owner-only.
  const canMutate = isOwner;

  // Restrict role options in the invite modal based on the actor's role.
  const invitableRoles = useMemo(() => {
    if (isOwner) return ALL_ROLES;
    if (currentRole === "admin_fped") return ["partner_operator", "viewer"];
    return [];
  }, [isOwner, currentRole]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data);
    } catch (e) {
      toast.error("Nu am putut încărca utilizatorii.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitInvite = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        email: invite.email.trim(),
        name: invite.name.trim(),
        role: invite.role,
      };
      if (invite.role === "partner_operator" && invite.assigned_org.trim()) {
        payload.assigned_org = invite.assigned_org.trim();
      }
      const r = await api.post("/admin/users", payload);
      const channel = r.data?.auth_channel === "magic_link"
        ? "I-am trimis un link de acces pe email."
        : "Va putea face login cu Google.";
      toast.success(`${payload.email} a fost adăugat. ${channel}`);
      setShowInvite(false);
      setInvite({ email: "", name: "", role: "viewer", assigned_org: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la invitație");
    }
  };

  const changeRole = (u, role) => {
    if (role === u.role) return;
    if (!window.confirm(roleChangeQuestion(u, role))) return;
    updateUser(u.id, { role });
  };

  const updateUser = async (id, patch) => {
    try {
      const r = await api.put(`/admin/users/${id}`, patch);
      toast.success(r.data?.sessions_revoked ? "Actualizat. Sesiunile lui au fost închise." : "Actualizat");
      load();
    } catch (err) {
      if (err?.response?.status === 404) {
        setUsers((prev) => prev.filter((x) => x.id !== id));
        toast.message("Utilizatorul nu mai există — listă reactualizată.");
        return;
      }
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Ștergi accesul lui ${u.email}?`)) return;
    const ref = u.id || u.email;
    try {
      await api.delete(`/admin/users/${encodeURIComponent(ref)}`);
      toast.success("Șters");
      setUsers((prev) => prev.filter((x) => x.email !== u.email));
    } catch (err) {
      if (err?.response?.status === 404) {
        setUsers((prev) => prev.filter((x) => x.email !== u.email));
        toast.message("Utilizatorul nu mai există — listă reactualizată.");
        return;
      }
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  const resendInvite = async (u) => {
    try {
      await api.post(`/admin/users/${u.id || u.email}/resend-invite`);
      toast.success(`Link de acces nou trimis către ${u.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la retrimitere");
    }
  };

  const revokeSessions = async (u) => {
    if (!window.confirm(`Revoci toate sesiunile lui ${u.email}? Va trebui să se autentifice din nou.`)) return;
    try {
      await api.post(`/admin/users/${u.id || u.email}/revoke-sessions`);
      toast.success(`Sesiuni revocate pentru ${u.email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la revocare");
    }
  };

  // Auth Faza 5 — owner-only impersonation. Backend mints a short JWT (30
  // min) and rotates the cookie; we hard-reload so all state (sidebar,
  // permissions, banner) re-bootstraps from /auth/me under the new role.
  const impersonate = async (u) => {
    const ok = window.confirm(
      `Ești sigur că vrei să impersonifici ${u.email}?\n\n` +
      `Vei vedea platforma cu rolul "${ROLE_LABEL[u.role] || u.role}" ` +
      `și permisiunile lui. Această acțiune va fi înregistrată în audit log.`,
    );
    if (!ok) return;
    try {
      await api.post(`/admin/users/${u.id}/impersonate`);
      toast.success(`Impersonezi acum: ${u.email}`);
      // Hard reload to /admin/users so everything (sidebar, permissions,
      // banner) rebuilds against the new identity. Symmetric with the stop
      // flow which also lands here so the owner sees a clear loop closure.
      // Cannot use SPA navigation here — useMe() caches the previous role.
      setTimeout(() => { window.location.href = "/admin/users"; }, 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la impersonare");
    }
  };

  const previewCleanup = async () => {
    setCleanupRunning(true);
    try {
      const r = await api.post("/admin/users/cleanup-legacy");
      setCleanupPreview(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la verificare");
    } finally {
      setCleanupRunning(false);
    }
  };

  const confirmCleanup = async () => {
    setCleanupRunning(true);
    try {
      const r = await api.post("/admin/users/cleanup-legacy?dry_run=false");
      const removedCount = (r.data?.removed || []).length;
      if (removedCount > 0) {
        toast.success(`${removedCount} utilizator${removedCount === 1 ? "" : "i"} legacy șters${removedCount === 1 ? "" : "i"}`);
      } else {
        toast.message("Niciun utilizator de șters.");
      }
      setCleanupPreview(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la ștergere");
    } finally {
      setCleanupRunning(false);
    }
  };

  const legacyCount = users.filter((u) => u.legacy).length;

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="text-sm text-[#6B7280]">
          {loading ? "Se încarcă…" : `${users.length} utilizator${users.length === 1 ? "" : "i"}`}
          {legacyCount > 0 && (
            <span className="ml-3 inline-flex items-center gap-1 rounded-full bg-[#E63946]/10 text-[#9b1f2c] px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest">
              <ShieldAlert size={11} /> {legacyCount} legacy
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isOwner && legacyCount > 0 && (
            <button
              onClick={previewCleanup}
              disabled={cleanupRunning}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#9b1f2c]/10 text-[#9b1f2c] hover:bg-[#9b1f2c]/20 px-3 py-2 text-xs font-semibold disabled:opacity-50"
              data-testid="users-cleanup-legacy-button"
            >
              <ShieldAlert size={14} /> Curăță legacy
            </button>
          )}
          {canInvite && (
            <button
              onClick={() => setShowInvite(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white px-4 py-2 text-sm font-semibold"
              data-testid="users-invite-button"
            >
              <Plus size={14} /> Invită utilizator
            </button>
          )}
        </div>
      </div>

      {!canMutate && (
        <div className="mb-4 rounded-xl bg-[#EDEEF0] border border-[#E5E7EB] p-3 text-sm text-[#6B7280] flex items-center gap-2" data-testid="users-readonly-banner">
          <span className="inline-flex rounded-full bg-[#6B7280]/15 text-[#373A45] px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">Read-only</span>
          Doar un <strong>owner</strong> poate modifica, dezactiva sau revoca sesiunile utilizatorilor existenți.
          {currentRole === "admin_fped" && " Tu poți doar invita parteneri sau vieweri noi."}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-[#EDEEF0] text-[#373A45]">
            <tr className="text-left">
              <Th>Email</Th>
              <Th>Nume</Th>
              <Th>Rol</Th>
              <Th>Canal</Th>
              <Th>Status</Th>
              <Th>Ultima autentificare</Th>
              <Th>Adăugat de</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && !loading && (
              <tr><td colSpan={8} className="p-6 text-center text-[#6B7280]">Niciun utilizator.</td></tr>
            )}
            {users.map((u) => {
              const isSelf = u.email === currentEmail;
              const status = deriveStatus(u);
              const channel = u.auth_channel || "google";
              return (
                <tr key={u.id} className="border-t border-[#E5E7EB]">
                  <Td>
                    <span className="font-medium text-[#373A45]">{u.email}</span>
                    {isSelf && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6B7280]">(tu)</span>}
                    {u.legacy && (
                      <span
                        className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#E63946]/10 text-[#9b1f2c] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest"
                        title="Cont seedat de scriptul vechi pre-OAuth. Recomandat: șterge."
                        data-testid={`users-legacy-${u.id}`}
                      >
                        <ShieldAlert size={10} /> legacy
                      </span>
                    )}
                    {u.assigned_org && (
                      <div className="text-[11px] text-[#6B7280] mt-0.5 inline-flex items-center gap-1">
                        <Globe size={10} /> {u.assigned_org}
                      </div>
                    )}
                  </Td>
                  <Td>{u.name || <span className="text-[#6B7280]">—</span>}</Td>
                  <Td>
                    {canMutate && !isSelf ? (
                      <select
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                        className={`rounded-full px-3 py-1 text-xs font-semibold border-0 ${ROLE_COLORS[u.role] || ROLE_COLORS.viewer}`}
                        data-testid={`users-role-${u.id}`}
                      >
                        {ALL_ROLES.map((r) => (
                          <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                        ))}
                      </select>
                    ) : (
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest ${ROLE_COLORS[u.role] || ROLE_COLORS.viewer}`}>
                        {ROLE_LABEL[u.role] || u.role}
                      </span>
                    )}
                    <div className={`mt-1 text-[11px] ${accessOf(u.role).key === "all" ? "text-[#6B7280]" : "text-[#8b5a06]"}`}
                      title={accessOf(u.role).title} data-testid={`users-access-${u.id}`}>
                      {accessOf(u.role).label}
                    </div>
                  </Td>
                  <Td>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${CHANNEL_COLORS[channel] || CHANNEL_COLORS.google}`}
                      data-testid={`users-channel-${u.id}`}
                    >
                      {channel === "magic_link" ? <Mail size={10} /> : <KeyRound size={10} />}
                      {CHANNEL_LABEL[channel] || channel}
                    </span>
                  </Td>
                  <Td>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[status]}`}
                      data-testid={`users-status-${u.id}`}
                    >
                      {status === "active" ? <UserCheck size={10} /> :
                       status === "invited" ? <Mail size={10} /> :
                       <UserX size={10} />}
                      {STATUS_LABEL[status] || status}
                    </span>
                  </Td>
                  <Td>
                    {u.last_login_at ? (
                      <span className="text-[#6B7280] text-xs inline-flex items-center gap-1">
                        <Clock size={10} /> {fmtDateTime(u.last_login_at)}
                      </span>
                    ) : (
                      <span className="text-[#6B7280] text-xs italic">niciodată</span>
                    )}
                  </Td>
                  <Td className="text-xs text-[#6B7280]">{u.invited_by || "—"}</Td>
                  <Td>
                    {canMutate && !isSelf && (
                      <UserRowActions
                        user={u}
                        status={status}
                        channel={channel}
                        isOwnerActor={isOwner}
                        onResendInvite={() => resendInvite(u)}
                        onRevokeSessions={() => revokeSessions(u)}
                        onToggleStatus={() => updateUser(u.id, { status: status === "active" ? "disabled" : "active" })}
                        onDelete={() => removeUser(u)}
                        onImpersonate={() => impersonate(u)}
                      />
                    )}
                    {canMutate && isSelf && channel === "magic_link" && (status === "invited" || status === "active") && (
                      // Self-row keeps just the resend (no destructive actions on yourself).
                      <button
                        onClick={() => resendInvite(u)}
                        className="inline-flex items-center gap-1 text-[11px] text-[#7A4986] hover:underline"
                        data-testid={`users-resend-${u.id}`}
                      >
                        <Send size={11} /> Retrimite link
                      </button>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showInvite && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => setShowInvite(false)}
          data-testid="users-invite-modal"
        >
          <form
            onSubmit={submitInvite}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl border border-[#E5E7EB] shadow-2xl w-full max-w-md"
          >
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h3 className="font-display text-lg font-bold text-[#373A45]">Invită utilizator</h3>
              <button type="button" onClick={() => setShowInvite(false)} className="text-[#6B7280] hover:text-[#373A45]" aria-label="Închide">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <Field label="Email *">
                <input
                  required
                  type="email"
                  placeholder="ex. ana@fped.org sau operator@partener.ro"
                  value={invite.email}
                  onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                  className={inp}
                  data-testid="users-invite-email"
                />
              </Field>
              <Field label="Nume (opțional)">
                <input
                  value={invite.name}
                  onChange={(e) => setInvite({ ...invite, name: e.target.value })}
                  className={inp}
                  data-testid="users-invite-name"
                />
              </Field>
              <Field label="Rol">
                <select
                  value={invite.role}
                  onChange={(e) => setInvite({ ...invite, role: e.target.value })}
                  className={inp}
                  data-testid="users-invite-role"
                >
                  {invitableRoles.map((r) => (
                    <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-[#6B7280]">
                  <strong>Owner</strong>: tot · <strong>Admin FPED</strong>: leaduri, înscrieri, grupe, contracte, plăți
                  + invitare parteneri (Setări curs doar le citește) ·{" "}
                  <strong>Partener</strong>: leadurile și înscrierile alocate lui · <strong>Viewer</strong>: doar citește.
                </p>
              </Field>
              {invite.role === "partner_operator" && (
                <Field label="Organizație (opțional)">
                  <input
                    placeholder="ex. Logiscool București"
                    value={invite.assigned_org}
                    onChange={(e) => setInvite({ ...invite, assigned_org: e.target.value })}
                    className={inp}
                    data-testid="users-invite-org"
                  />
                  <p className="mt-1 text-[11px] text-[#6B7280]">
                    Apare ca etichetă pe rândul utilizatorului. Filtrarea reală a datelor pe organizație va fi activată în Faza 2.
                  </p>
                </Field>
              )}
              <div className="rounded-xl bg-[#F5F0F8] border border-[#E5D6EC] p-3 text-xs text-[#7A4986]">
                {(() => {
                  const looksFped = invite.email.toLowerCase().endsWith("@fped.org");
                  if (looksFped) {
                    return <>Utilizatorul va putea face login cu Google folosind acest email. Apare în listă imediat ca <strong>activ</strong>.</>;
                  }
                  return <>Utilizatorul va primi un email cu un link unic de acces (<strong>magic link</strong>, valabil 15 minute). Apare ca <strong>invitat</strong> până la prima autentificare.</>;
                })()}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-5 border-t border-[#E5E7EB] bg-[#F5F6F6]/60">
              <button type="button" onClick={() => setShowInvite(false)} className="rounded-full px-4 py-2 text-sm border border-[#E5E7EB] hover:bg-white">
                Anulează
              </button>
              <button
                type="submit"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white px-5 py-2 text-sm font-semibold"
                data-testid="users-invite-save"
              >
                <Save size={14} /> Adaugă
              </button>
            </div>
          </form>
        </div>
      )}

      {cleanupPreview && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => !cleanupRunning && setCleanupPreview(null)}
          data-testid="users-cleanup-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl border border-[#E5E7EB] shadow-2xl w-full max-w-lg"
          >
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h3 className="font-display text-lg font-bold text-[#9b1f2c] inline-flex items-center gap-2">
                <ShieldAlert size={18} /> Curățare utilizatori legacy
              </h3>
              <button type="button" onClick={() => setCleanupPreview(null)} disabled={cleanupRunning} className="text-[#6B7280] hover:text-[#373A45] disabled:opacity-50" aria-label="Închide">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4 text-sm text-[#373A45]">
              <p>
                Următor{cleanupPreview.candidates.length === 1 ? "ul cont va fi șters" : "ii conturi vor fi șterse"} permanent.
                Sunt conturi seedate de scriptul vechi pre-OAuth (cu <code className="bg-[#EDEEF0] px-1 py-0.5 rounded text-xs">password_hash</code>)
                sau care folosesc emailuri demo.
              </p>
              {cleanupPreview.candidates.length === 0 ? (
                <div className="rounded-xl bg-[#519872]/10 border border-[#519872]/30 p-3 text-[#2f6b50] text-sm">
                  Niciun cont legacy de șters.
                </div>
              ) : (
                <ul className="rounded-xl border border-[#E5E7EB] divide-y divide-[#E5E7EB] overflow-hidden" data-testid="users-cleanup-candidates">
                  {cleanupPreview.candidates.map((c) => (
                    <li key={c.email} className="px-3 py-2 flex items-center justify-between">
                      <span className="font-mono text-[13px] text-[#373A45]">{c.email}</span>
                      <span className="text-[10px] uppercase tracking-widest text-[#6B7280]">{c.role || "—"}</span>
                    </li>
                  ))}
                </ul>
              )}
              {cleanupPreview.skipped?.length > 0 && (
                <div className="rounded-xl bg-[#EDEEF0] border border-[#E5E7EB] p-3 text-xs text-[#6B7280]">
                  <strong className="text-[#373A45]">Sărite ({cleanupPreview.skipped.length}):</strong>
                  <ul className="mt-1.5 space-y-0.5">
                    {cleanupPreview.skipped.map((s) => (
                      <li key={s.email}>
                        <span className="font-mono">{s.email}</span> — {s.reason === "self" ? "ești tu" : s.reason === "last_active_owner" ? "ultimul owner activ" : s.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 p-5 border-t border-[#E5E7EB] bg-[#F5F6F6]/60">
              <button
                type="button"
                onClick={() => setCleanupPreview(null)}
                disabled={cleanupRunning}
                className="rounded-full px-4 py-2 text-sm border border-[#E5E7EB] hover:bg-white disabled:opacity-50"
                data-testid="users-cleanup-cancel"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={confirmCleanup}
                disabled={cleanupRunning || cleanupPreview.candidates.length === 0}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#9b1f2c] hover:bg-[#7e1923] text-white px-5 py-2 text-sm font-semibold disabled:opacity-40"
                data-testid="users-cleanup-confirm"
              >
                <Trash2 size={14} /> {cleanupRunning ? "Se șterge…" : `Șterge ${cleanupPreview.candidates.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full bg-white border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-[#373A45] focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#373A45] uppercase tracking-widest">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
const Th = ({ children }) => <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 text-[#373A45] align-middle ${className}`}>{children}</td>;

/**
 * Per-row action menu. Single ⋯ trigger that opens a Radix DropdownMenu
 * portaled to body — keeps row height constant and avoids table reflow.
 *
 * Rendered ONLY when `canMutate && !isSelf` (handled by the caller); the
 * permission filtering inside still honors per-row status (e.g., revoke
 * sessions only when user.status === "active", resend only for magic-link
 * users with status invited/active).
 */
function UserRowActions({ user, status, channel, isOwnerActor, onResendInvite, onRevokeSessions, onToggleStatus, onDelete, onImpersonate }) {
  const canResend = channel === "magic_link" && (status === "invited" || status === "active");
  const canRevoke = status === "active";
  // Auth Faza 5 — impersonate is owner-only, only for ACTIVE non-owner targets.
  const canImpersonate = isOwnerActor && status === "active" && user.role !== "owner";
  return (
    <div className="flex justify-end">
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title="Acțiuni"
            aria-label="Acțiuni utilizator"
            className="inline-flex items-center justify-center rounded-full p-1.5 border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6] focus:outline-none focus:ring-2 focus:ring-[#965EA7]"
            data-testid={`users-actions-${user.id}`}
          >
            <MoreHorizontal size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {canImpersonate && (
            <DropdownMenuItem
              onClick={onImpersonate}
              data-testid={`users-impersonate-${user.id}`}
              className="text-[#D97706] focus:text-[#D97706]"
            >
              <UserCog size={14} className="mr-2" /> Impersonare
            </DropdownMenuItem>
          )}
          {canResend && (
            <DropdownMenuItem
              onClick={onResendInvite}
              data-testid={`users-resend-${user.id}`}
              className="text-[#7A4986] focus:text-[#7A4986]"
            >
              <Send size={14} className="mr-2" /> Retrimite link
            </DropdownMenuItem>
          )}
          {canRevoke && (
            <DropdownMenuItem
              onClick={onRevokeSessions}
              data-testid={`users-revoke-${user.id}`}
              className="text-[#8b5a06] focus:text-[#8b5a06]"
            >
              <RotateCcw size={14} className="mr-2" /> Revocă sesiuni
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={onToggleStatus}
            data-testid={`users-toggle-${user.id}`}
          >
            {status === "active" ? (
              <><UserX size={14} className="mr-2" /> Dezactivează</>
            ) : (
              <><UserCheck size={14} className="mr-2" /> Activează</>
            )}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onDelete}
            data-testid={`users-delete-${user.id}`}
            className="text-[#9b1f2c] focus:text-[#9b1f2c]"
          >
            <Trash2 size={14} className="mr-2" /> Șterge
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
